import React, { useReducer, useEffect, useMemo, useCallback } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import { UserInfo } from 'firebase/auth';
import { getPiercingLocations } from '../../services/global';
import { auth } from '../../firebase';
import {
  createFullUser,
  getCustomTokenForUser,
  getUser,
} from '../../services/user';
import { getEmployee } from '../../services/employee';
import { getBusiness } from '../../services/business';
import { AppContext, AppState, AppStateAction, initialState } from './context';
import { isReactNativeWebView } from '../../utils/global/helper';
import Emitter from '../../constants/emitter';
import { handleSignOut } from '../../helpers/global';

const eventEmitter = Emitter.getInstance();

const appReducer = (state: AppState, action: AppStateAction) => {
  switch (action.type) {
    case 'SET_USER':
      return {
        ...state,
        user: {
          ...state.user,
          ...action.payload,
        },
      };

    case 'SET_SELECTED_BUSINESS':
      sessionStorage.setItem('selectedBusiness', action.payload);
      return {
        ...state,
        user: {
          ...state.user,
          selectedBusiness: action.payload,
          isLoading: true,
        },
      };

    case 'SET_MOST_RECENT_PAYMENTID':
      return {
        ...state,
        checkout: {
          ...state.checkout,
          mostRecentPaymentId: action.payload,
        },
      };

    case 'SET_PIERCING_LOCATIONS':
      return { ...state, piercingLocations: action.payload };

    case 'SET_SELECTED_APPOINTMENT':
      return { ...state, selectedAppointment: action.payload };

    case 'SET_OPENED_APPOINTMENT':
      return { ...state, openedAppointment: action.payload };

    case 'SET_WAIVERS_TABLE_PAGE':
      return { ...state, waiversTablePage: action.payload };

    case 'SET_SELECTED_APPOINTMENT_UPDATED':
      return { ...state, selectedAppointmentUpdated: action.payload };

    case 'SET_CHECKOUT_MODAL_OPEN':
      return {
        ...state,
        checkout: {
          ...state.checkout,
          checkoutModalOpen: action.payload,
        },
      };

    case 'SET_CHECKOUT_FAILURE_DATA':
      return {
        ...state,
        checkout: {
          ...state.checkout,
          checkoutFailureData: action.payload,
        },
      };

    case 'SET_TERMINAL_CHECKOUT_MODAL':
      return {
        ...state,
        checkout: {
          ...state.checkout,
          terminalCheckoutModal: action.payload,
        },
      };

    case 'SET_CONFIRMATION_MODAL': {
      return {
        ...state,
        confirmationModal: {
          ...state.confirmationModal,
          ...action.payload,
        },
      };
    }

    case 'SET_LOADING_MODAL': {
      return {
        ...state,
        loadingModal: {
          ...state.loadingModal,
          ...action.payload,
        },
      };
    }

    case 'SET_SUB_MODAL_DISMISSED': {
      return {
        ...state,
        isSubModalDismissed: action.payload,
      };
    }

    case 'SET_SEARCH_MODAL': {
      return {
        ...state,
        searchModal: {
          ...state.searchModal,
          ...action.payload,
        },
      };
    }

    case 'SET_UNREAD_CONVERSATIONS': {
      return {
        ...state,
        unreadConversations: action.payload,
      };
    }

    case 'SET_AWAITED_FORM_MODAL': {
      return {
        ...state,
        awaitedFormModal: {
          ...state.awaitedFormModal,
          ...action.payload,
        },
      };
    }

    case 'SET_SELECTED_SPLIT_PAYMENTS_GROUP_ID': {
      return {
        ...state,
        selectedSplitPaymentsGroupId: action.payload,
      };
    }

    case 'SET_IS_IN_SPLIT_CHECKOUT': {
      return {
        ...state,
        checkout: { ...state.checkout, isInSplitCheckout: action.payload },
      };
    }

    default:
      return state;
  }
};

interface AppProviderProps {
  children: React.ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [, authLoading] = useAuthState(auth as any);
  const [state, dispatch] = useReducer(appReducer, initialState);
  const { selectedBusiness } = state.user;

  const navigate = useNavigate();

  useEffect(() => {
    // Fetch piercing locations and update state
    getPiercingLocations().then(locations => {
      dispatch({ type: 'SET_PIERCING_LOCATIONS', payload: locations });
    });
  }, []);

  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]); // Dependencies array

  const signInFromToken = useCallback(async (token: string) => {
    try {
      const customToken = await getCustomTokenForUser(token);
      if (customToken) {
        await auth.signInWithCustomToken(customToken);
      }
    } catch {
      /* empty */
    }
  }, []);

  useEffect(() => {
    if (!isReactNativeWebView()) return undefined;

    const authTokenListener = async (event: MessageEvent) => {
      try {
        if (!event.data || typeof event.data !== 'string') return;
        const data = JSON.parse(event.data);
        if (data.type === 'AUTH_TOKEN' && data.token) {
          await signInFromToken(data.token);
        }
      } catch {
        // Do nothing
      }
    };

    window.addEventListener('message', authTokenListener);

    return () => {
      window.removeEventListener('message', authTokenListener);
    };
  }, [signInFromToken]);

  const getUserData = useCallback(
    async (user: UserInfo | null, signOutIfError = true) => {
      try {
        const tempUser = await getUser();
        if (
          tempUser?.role !== 'EMPLOYEE' &&
          tempUser?.role !== 'SHOP_MANAGER'
        ) {
          dispatch({
            type: 'SET_USER',
            payload: { data: tempUser, isLoading: false },
          });
          navigate('/signup');
          return;
        }

        const tempEmployee = await getEmployee(tempUser.userId);
        const businesses = await Promise.all(
          tempEmployee.employeesToBusinesses.map(
            ({ businessId }: { businessId: string }) => getBusiness(businessId),
          ),
        );

        if (!businesses || !tempEmployee) {
          dispatch({
            type: 'SET_USER',
            payload: { data: tempUser, isLoading: false },
          });
          navigate('/signup');
          return;
        }

        const tempLoadedUser = createFullUser(
          tempUser,
          businesses,
          tempEmployee,
          selectedBusiness,
        );

        dispatch({
          type: 'SET_USER',
          payload: { data: tempLoadedUser, isLoading: false },
        });
      } catch (error) {
        if (signOutIfError) {
          await handleSignOut();
          dispatch({
            type: 'SET_USER',
            payload: { data: null, isLoading: false },
          });
        }
      }
    },
    [navigate, selectedBusiness],
  );

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async user => {
      if (
        authLoading ||
        // do not fetch user data if user is already loaded and the user is the same
        // this is triggered on every page load
        (user &&
          state.user.data?.authServiceId &&
          user.uid === state.user.data?.authServiceId)
      )
        return;
      await getUserData(user);
    });

    return () => unsubscribe();
  }, [authLoading, getUserData, state.user.data?.authServiceId]);

  const reloadUser = useCallback(
    async (signOutIfError = false) => {
      await getUserData(auth.currentUser, signOutIfError);
    },
    [getUserData],
  );

  useEffect(() => {
    const handleUserReload = async () => {
      await reloadUser();
      eventEmitter.emit('userReloaded');
    };

    eventEmitter.on('triggerUserReload', handleUserReload);

    return () => {
      eventEmitter.off('triggerUserReload', handleUserReload);
    };
  }, [reloadUser]);

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
}
